/*
 * Android-only glue between the web app and native code:
 *  - keeps the home-screen widget snapshot and reminder alarms in step with the replica
 *  - opens deep links from widgets/notifications (engram://note/<id>, engram://new-note, …)
 *  - hardware back button, sync on resume
 */
import { App as CapApp } from "@capacitor/app";
import { navigate } from "wouter/use-browser-location";
import { isNative } from "./api";
import { createNote } from "./db/actions";
import { openTaskDialog } from "./ui/TaskDialog";
import { db, onTablesChanged } from "./db/client";
import { EngramNative, type Reminder } from "./native";

let started = false;

export function startNativeBridge() {
  if (!isNative || started) return;
  started = true;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(() => void pushNativeState(), 1500);
  };
  onTablesChanged((t) => {
    if (t.has("notes") || t.has("tasks") || t.has("shortcuts") || t.has("notebooks") || t.has("tags") || t.has("saved_searches") || t.has("settings")) refresh();
  });
  refresh();

  void CapApp.addListener("appUrlOpen", ({ url }) => void openUrl(url));
  void CapApp.getLaunchUrl().then((r) => {
    if (r?.url) void openUrl(r.url);
  });
  void CapApp.addListener("backButton", ({ canGoBack }) => {
    const layers = document.querySelectorAll<HTMLElement>(".modal-backdrop, .popover");
    const top = layers[layers.length - 1];
    if (top) {
      // React listens at its root: dispatch from inside the dialog (its focused field, or the dialog itself), bubbling.
      const target = document.activeElement instanceof HTMLElement && top.contains(document.activeElement) ? document.activeElement : (top.querySelector<HTMLElement>(".modal, .menu, [role=dialog]") ?? top);
      target.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }));
      // Still open (a dialog without an Escape handler): a click on its backdrop closes it.
      setTimeout(() => {
        if (top.isConnected && top.classList.contains("modal-backdrop")) top.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      }, 50);
      return;
    }
    if (canGoBack && location.pathname !== "/home") history.back();
    else void CapApp.minimizeApp();
  });
  void CapApp.addListener("resume", () => void db.sync());
  void CapApp.addListener("pause", () => void pushNativeState());
  void EngramNative.requestNotificationPermission().catch(() => undefined);
}

async function openUrl(url: string) {
  const u = new URL(url);
  // engram://note/<id> → host "note", path "/<id>"
  const [kind, id] = [u.host, u.pathname.replace(/^\//, "")];
  switch (kind) {
    case "note":
      if (id) navigate(`/note/${id}`);
      break;
    case "new-note":
      navigate(`/notes?n=${await createNote()}`);
      break;
    case "new-task":
      // Open the New task dialog (a task needs a title), not a blank "Untitled task".
      navigate("/tasks");
      openTaskDialog();
      break;
    case "search":
      navigate("/search?focus=1");
      break;
    case "tasks":
      navigate("/tasks");
      break;
    case "shortcut":
      navigate(decodeURIComponent(id ?? "/shortcuts"));
      break;
    case "auth": // Google sign-in hand-off; handled by the sign-in screen
      break;
    default:
      navigate("/home");
  }
}

async function pushNativeState() {
  const [notes, tasks, shortcuts, reminders, scratch, cursor] = await Promise.all([
    db.query<{ id: string; title: string; snippet: string; updated: number }>(
      "SELECT id, title, substr(body_text, 1, 120) AS snippet, updated FROM notes WHERE deleted = 0 AND trashed_at IS NULL ORDER BY updated DESC LIMIT 25",
    ),
    // The List and To-do widgets (the To-do widget sorts and filters them itself: today's, flagged, the rest).
    db.query<{ id: string; title: string; due_at: number | null; due_all_day: number; flagged: number; note_id: string; recurrence: string | null }>(
      `SELECT t.id, t.title, t.due_at, t.due_all_day, t.flagged, t.note_id, t.recurrence FROM tasks t JOIN notes n ON n.id = t.note_id
       WHERE t.deleted = 0 AND t.completed_at IS NULL AND n.deleted = 0 AND n.trashed_at IS NULL
       ORDER BY (t.due_at IS NULL), t.due_at, t.created LIMIT 100`,
    ),
    db.query<{ kind: string; target_id: string; label: string | null }>(
      `SELECT s.kind, s.target_id, COALESCE(
          (SELECT title FROM notes WHERE id = s.target_id AND deleted = 0),
          (SELECT name FROM notebooks WHERE id = s.target_id AND deleted = 0),
          (SELECT name FROM tags WHERE id = s.target_id AND deleted = 0),
          (SELECT name FROM saved_searches WHERE id = s.target_id AND deleted = 0)) AS label
       FROM shortcuts s WHERE s.deleted = 0 ORDER BY s.position LIMIT 25`,
    ),
    db.query<{ id: string; title: string; reminder_at: number; note_id: string; note_title: string }>(
      `SELECT t.id, t.title, t.reminder_at, t.note_id, n.title AS note_title FROM tasks t JOIN notes n ON n.id = t.note_id
       WHERE t.deleted = 0 AND t.completed_at IS NULL AND t.reminder_at IS NOT NULL AND t.reminder_at > ? AND n.deleted = 0 AND n.trashed_at IS NULL
       ORDER BY t.reminder_at LIMIT 200`,
      [Date.now() - 60_000],
    ),
    db.get<{ value: string; updated: number }>("SELECT value, updated FROM settings WHERE id = 'scratchPad' AND deleted = 0"),
    db.get<{ value: string }>("SELECT value FROM kv WHERE key = 'cursor'"),
  ]);
  let scratchText = "";
  try {
    const v = scratch ? (JSON.parse(scratch.value) as unknown) : "";
    if (typeof v === "string") scratchText = v;
  } catch {
    /* unreadable: the widget keeps what it has */
  }
  const shortcutPath = (kind: string, id: string) =>
    kind === "note" ? `/note/${id}` : kind === "notebook" ? `/notebook/${id}` : kind === "stack" ? `/stack/${id}` : kind === "tag" ? `/tag/${id}` : `/search?saved=${id}`;
  const snapshot = {
    updated: Date.now(),
    // How far this copy has synced. A task ticked or added on the home screen and sent at a later seq isn't in it
    // yet, so the widgets keep showing that change until a snapshot past it arrives (Todo.kt).
    cursor: Number(cursor?.value ?? 0),
    notes: notes.map((n) => ({ id: n.id, title: n.title || "Untitled", snippet: n.snippet, updated: n.updated })),
    tasks: tasks.map((t) => ({ id: t.id, title: t.title || "Untitled task", due: t.due_at, allDay: !!t.due_all_day, flagged: !!t.flagged, noteId: t.note_id })),
    shortcuts: shortcuts.map((s) => ({ kind: s.kind, title: s.label ?? "Untitled", url: `engram://shortcut/${encodeURIComponent(shortcutPath(s.kind, s.target_id))}` })),
    // The Scratch Pad widget keeps whichever copy is newer (this one, or an edit made on the home screen).
    ...(scratch ? { scratch: { text: scratchText, updated: scratch.updated } } : {}),
  };
  await EngramNative.updateWidgets({ snapshot: JSON.stringify(snapshot) }).catch((e) => console.warn("widgets", e));
  const list: Reminder[] = reminders.map((r) => ({ id: r.id, title: r.title || "Reminder", body: r.note_title || "", at: r.reminder_at, noteId: r.note_id }));
  await EngramNative.scheduleReminders({ reminders: list }).catch((e) => console.warn("reminders", e));
}
